import {
  INQUIRY_CONSTANTS,
  QUERY_CONSTANTS,
} from '@/constants';

import { DateTime } from 'luxon';

import {
  aggregator_user_data_repository,
  customer_inquiry_repository,
  query_repository,
  search_repository,
  sequence_repository,
} from '@/repositories';

const state = () => ({
  contact: null,
  contact_main_job: null,
  contact_has_opted_out_from_base: null,
  contact_has_opted_out_from_email: null,
  contact_is_inactive: false,
  contact_must_be_ignored: false,
  contact_other_mandate_list: [],
  contact_was_created_following_inquiry: null,
  contact_with_aggregated_data: null,
  failed_inquiry_exists_for_contact: false,
  open_inquiry_exists_for_contact: false,
});

const getters = {
  contact: (state) => state.contact,
  contact_has_opted_out_from_base:  (state) => state.contact_has_opted_out_from_base,
  contact_has_opted_out_from_email:  (state) => state.contact_has_opted_out_from_email,
  contact_is_inactive: (state) => state.contact_is_inactive,
  contact_main_job: (state) => state.contact_main_job,
  contact_must_be_ignored: (state) => state.contact_must_be_ignored,
  contact_other_mandate_list: (state) => state.contact_other_mandate_list,
  contact_was_created_following_inquiry: (state) => state.contact_was_created_following_inquiry,
  contact_with_aggregated_data: (state) => state.contact_with_aggregated_data,
  failed_inquiry_exists_for_contact: (state) => state.failed_inquiry_exists_for_contact,
  open_inquiry_exists_for_contact: (state) => state.open_inquiry_exists_for_contact,
};

const actions = {
  async getContactById({ commit, dispatch }, contact_id) {
    let contact;

    try {
      contact = await search_repository.getContactById(contact_id);
    } catch (error) {
      if (error?.response?.status !== 404) {
        throw error;
      }

      contact = {
        optout: 'OPTOUT_BASE',
      };
    }

    commit('set_contact_is_inactive', contact?.active === false);
    commit('set_contact_has_opted_out_from_base', contact?.optout === 'OPTOUT_BASE');
    commit('set_contact_has_opted_out_from_email', contact?.optout === 'OPTOUT_EMAIL');

    commit('set_contact', contact);

    const contact_active_job_list = contact?.job_list?.filter((job) => job.active) || [];

    let main_job = contact_active_job_list?.find((job) => job.main) || null;

    if (!main_job && contact_active_job_list?.length > 0) {
      main_job = [...contact_active_job_list]
        ?.sort((a, b) => (b.last_revision.date).localeCompare(a.last_revision.date))
        ?.at(0) || null;
    }

    commit('set_contact_main_job', main_job);

    const other_mandates = contact_active_job_list?.filter((job) => !job.main) || [];

    commit('set_contact_other_mandate_list', other_mandates);

    const contact_was_created_following_inquiry = await dispatch('checkIfContactWasCreatedFollowingInquiry', {
      contact_id: contact.id,
    });

    commit('set_contact_was_created_following_inquiry', contact_was_created_following_inquiry);

    return contact;
  },
  async getAssistedContactById(_, contact_id) {
    const contact = await search_repository.getContactById(contact_id);

    return contact;
  },
  async getContactWithAggregatedData({ commit }, criteria) {
    const result = await aggregator_user_data_repository.searchContacts({
      ...criteria,
      matched_queries_query_type_id_list: [
        QUERY_CONSTANTS.TYPES.IGNORE_ENGAGE_CONTACT_LIST,
        QUERY_CONSTANTS.TYPES.IGNORE_CAMPAIGN_CONTACT_LIST,
        QUERY_CONSTANTS.TYPES.IGNORE_CUSTOM_CONTACT_LIST,
        QUERY_CONSTANTS.TYPES.INTERACTION_MAIL_BOUNCED,
        QUERY_CONSTANTS.TYPES.PROSPECTING_CONTACT,
        QUERY_CONSTANTS.TYPES.INTERACTION_MAIL_REPLIED,
      ],
    });

    result.contact_list.forEach((contact) => {
      contact.ignore_query_list = contact.query_list
        .filter((query) => [
          QUERY_CONSTANTS.TYPES.IGNORE_ALL_CONTACT_LIST,
          QUERY_CONSTANTS.TYPES.IGNORE_ENGAGE_CONTACT_LIST,
          QUERY_CONSTANTS.TYPES.IGNORE_CAMPAIGN_CONTACT_LIST,
          QUERY_CONSTANTS.TYPES.IGNORE_CUSTOM_CONTACT_LIST,
        ].includes(query.query_type_id));

      contact.query_list = contact.query_list
        .filter((query) => [
          QUERY_CONSTANTS.TYPES.INTERACTION_MAIL_BOUNCED,
          QUERY_CONSTANTS.TYPES.PROSPECTING_CONTACT,
        ].includes(query.query_type_id));
    });

    commit('set_contact_with_aggregated_data', result?.contact_list?.at(0) || null);

    return result;
  },
  async getContactOpenInquiryCountByType(_, input) {
    const {
      customer_inquiry_count,
    } = await customer_inquiry_repository.searchCustomersInquiry({
      contact_id_list: [input.contact_id],
      inquiry_type_id_list: [input.type_id],
      limit: 0,
      status_id_list: [
        INQUIRY_CONSTANTS.STATUS_ID.DRAFT,
        INQUIRY_CONSTANTS.STATUS_ID.TODO,
        INQUIRY_CONSTANTS.STATUS_ID.IN_PROGRESS,
      ],
    });

    return customer_inquiry_count;
  },
  async getCustomerInquiriesForSpecificContact({ commit }, input) {
    const {
      failed_inquiry_exists,
      open_inquiry_exists,
    } = await customer_inquiry_repository.getCustomerInquiriesForSpecificContact(input);

    commit('set_failed_inquiry_exists_for_contact', failed_inquiry_exists);
    commit('set_open_inquiry_exists_for_contact', open_inquiry_exists);

    return {
      failed_inquiry_exists,
      open_inquiry_exists,
    };
  },
  async getContactSegments({ rootState }, criteria) {
    const updated_criteria = {
      query_type_id_list: [
        QUERY_CONSTANTS.TYPES.SEGMENT_CONTACT,
        QUERY_CONSTANTS.TYPES.ADDRESS_BOOK,
      ],
      sort: ['-modification_date'],
      ...criteria,
    };

    const {
      query_list,
    } = await query_repository.searchUserQueryList(
      rootState.user.token_data.user_id,
      updated_criteria
    );

    return query_list;
  },
  async searchInteractions(_, input) {
    const {
      enriched_interactions_list,
    } = await aggregator_user_data_repository.searchInteractions(input);

    return enriched_interactions_list?.at(0) || [];
  },
  async searchSequencesIncludingContacts(_, input) {
    const {
      sequence_list,
    } = await sequence_repository.searchSequencesIncludingContacts(input);

    return sequence_list;
  },
  async searchSequences(_, criteria) {
    const {
      sequence_list,
    } = await sequence_repository.searchSequences(criteria);

    return sequence_list;
  },
  async searchTasks(_, criteria) {
    const {
      task_list,
    } = await aggregator_user_data_repository.searchTasks(criteria);

    return task_list;
  },

  async getTarget(_, { sequence_id, criteria }) {
    const response = await sequence_repository.getTargets(sequence_id, criteria);

    return response.target_list?.at(0);
  },

  async getSequenceStepList(_, sequence_id) {
    const result = await sequence_repository.searchSequenceSteps(sequence_id);

    return result.step_list;
  },

  async updateTarget(_, { sequence_id, target_id, data }) {
    const updated_target = await sequence_repository.updateTarget(sequence_id, target_id, data);

    return updated_target;
  },

  async getActiveNews(_, input) {
    const {
      news_list,
    } = await search_repository.getActiveNews(input);

    return news_list;
  },

  async checkIfContactWasCreatedFollowingInquiry(_, input) {
    const {
      customer_inquiry_count,
    } = await customer_inquiry_repository.searchCustomersInquiry({
      contact_id_list: [input.contact_id],
      closure_date_lower_boundary: DateTime
        .utc()
        .minus({
          months: 3,
        })
        .toISO(),
      inquiry_sub_type_id_list: [INQUIRY_CONSTANTS.INQUIRY_SUB_TYPE_ID.SEARCH_DECISION_MAKER],
      limit: 0,
      status_id_list: [
        INQUIRY_CONSTANTS.STATUS_ID.DONE,
      ],
    });

    return customer_inquiry_count > 0;
  },
};

const mutations = {
  set_contact(state, contact) {
    state.contact = contact;
  },
  set_contact_has_opted_out_from_base(state, boolean) {
    state.contact_has_opted_out_from_base = boolean;
  },
  set_contact_has_opted_out_from_email(state, boolean) {
    state.contact_has_opted_out_from_email = boolean;
  },
  set_contact_is_inactive(state, boolean) {
    state.contact_is_inactive = boolean;
  },
  set_contact_main_job(state, contact_main_job) {
    state.contact_main_job = contact_main_job;
  },
  set_contact_must_be_ignored(state, boolean) {
    state.contact_must_be_ignored = boolean;
  },
  set_contact_other_mandate_list(state, other_mandate_list) {
    state.contact_other_mandate_list = other_mandate_list;
  },
  set_contact_was_created_following_inquiry(state, boolean) {
    state.contact_was_created_following_inquiry = boolean;
  },
  set_contact_with_aggregated_data(state, contact_with_aggregated_data) {
    state.contact_with_aggregated_data = contact_with_aggregated_data;
  },
  set_failed_inquiry_exists_for_contact(state, boolean) {
    state.failed_inquiry_exists_for_contact = boolean;
  },
  set_open_inquiry_exists_for_contact(state, boolean) {
    state.open_inquiry_exists_for_contact = boolean;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
